/**
 * The messages this browser has handed to the server but not yet read back.
 *
 * ## Why not the query cache
 *
 * The placeholder used to be patched into the conversation's infinite-query cache.
 * That works for one send at a time and breaks the moment two overlap: every
 * successful POST makes `@birdwatch/client` invalidate the whole `["channels"]`
 * prefix, and the resulting refetch REPLACES the cache — taking any other in-flight
 * send's placeholder with it. The composer now hands the box back at press time
 * (`sendMode: "instant"`), so overlapping sends are the normal case, and a
 * message that blinks out of the thread for a round trip is exactly what the user
 * would report next.
 *
 * Client state is where a not-yet-persisted message belongs. Nothing the server
 * says can erase it, and {@link mergePendingMessages} drops it the moment the
 * fetched list carries the real row.
 *
 * ## Module scope, not a ref
 *
 * The composer is remounted by `key={cid}` (`channel-view.tsx`) and the whole view
 * is keyed again in the office inbox, and two mounted surfaces can send into one
 * channel (the visit dialogs sit beside the composer). A per-component ref would
 * lose entries on a remount and keep two disagreeing lists; one module-level
 * registry is the single answer for a cid.
 *
 * Not persisted: a placeholder describes a request this page load is making, so it
 * must not survive a reload that has already abandoned that request.
 */

import { create } from "zustand";

import type { ChannelMessage } from "../_api/channel-endpoints";
import {
  dropLandedPendingSends,
  type PendingSend,
} from "../_libs/optimistic-message";

/**
 * Shared identity for a channel with nothing pending.
 *
 * zustand's `useStore` is `useSyncExternalStore(subscribe, () => selector(getState()))`
 * with no equality function, so a selector that builds a fresh `[]` for an unknown
 * cid returns a new reference on every read and re-renders forever. One frozen empty
 * array keeps the miss case stable.
 */
const NO_PENDING: PendingSend[] = [];

type PendingSendsStore = {
  /** Oldest press first, per channel. */
  byChannel: Record<string, PendingSend[] | undefined>;
  add: (cid: string, message: ChannelMessage) => void;
  markSent: (cid: string, optimisticId: string, sentId: string) => void;
  remove: (cid: string, optimisticId: string) => void;
  reapLanded: (cid: string, messages: ChannelMessage[]) => void;
};

/**
 * Private — the module-level writers below are the whole surface, so nothing can
 * reach past them into `setState`.
 *
 * Every action spreads `byChannel` but replaces only its own cid's array, so a send
 * into one channel leaves every other channel's array identity untouched and those
 * threads never re-render for it.
 */
const usePendingSendsStore = create<PendingSendsStore>()((set, get) => ({
  byChannel: {},

  add: (cid, message) =>
    set((state) => ({
      byChannel: {
        ...state.byChannel,
        [cid]: [...(state.byChannel[cid] ?? []), { message }],
      },
    })),

  markSent: (cid, optimisticId, sentId) => {
    const current = get().byChannel[cid];
    if (!current) return;

    set((state) => ({
      byChannel: {
        ...state.byChannel,
        [cid]: current.map((pending) =>
          pending.message.id === optimisticId
            ? { ...pending, sentId }
            : pending,
        ),
      },
    }));
  },

  remove: (cid, optimisticId) => {
    const current = get().byChannel[cid];
    if (!current) return;

    set((state) => ({
      byChannel: {
        ...state.byChannel,
        [cid]: current.filter((pending) => pending.message.id !== optimisticId),
      },
    }));
  },

  reapLanded: (cid, messages) => {
    const current = get().byChannel[cid];
    if (!current) return;

    const kept = dropLandedPendingSends(current, messages);
    // Same array back means nothing landed. Skipping the `set` is what lets the
    // thread call this on every data change without notifying every subscriber.
    if (kept === current) return;

    set((state) => ({ byChannel: { ...state.byChannel, [cid]: kept } }));
  },
}));

/** Show `message` in `cid`'s thread until the server's own copy arrives. */
export function addPendingSend(cid: string, message: ChannelMessage): void {
  usePendingSendsStore.getState().add(cid, message);
}

/**
 * Record the id the POST answered with.
 *
 * This — not the removal below — is what ends the placeholder's life: the merge
 * hides it as soon as the fetched page contains `sentId`, so the swap happens in a
 * single render and can neither show the message twice nor leave a hole between
 * the two.
 */
export function markPendingSent(
  cid: string,
  optimisticId: string,
  sentId: string,
): void {
  usePendingSendsStore.getState().markSent(cid, optimisticId, sentId);
}

/** Drop the entry — the send settled, one way or the other. */
export function removePendingSend(cid: string, optimisticId: string): void {
  usePendingSendsStore.getState().remove(cid, optimisticId);
}

/**
 * Forget the entries whose real row is now in `messages`.
 *
 * Landing — not "some refetch finished" — is what ends a placeholder's life, and the
 * two are NOT the same event: `invalidateQueries` cancels an in-flight refetch by
 * default, so a send that awaited its own invalidation could resume with the row
 * still absent and drop a placeholder the reader still needs. Only the fetched list
 * can answer "is it there yet", so the list is what prunes the registry.
 *
 * Returns without notifying when nothing landed, so a caller may run it on every
 * render of the thread.
 */
export function reapLandedPendingSends(
  cid: string,
  messages: ChannelMessage[],
): void {
  usePendingSendsStore.getState().reapLanded(cid, messages);
}

/** This channel's unsent messages, oldest press first. */
export function usePendingSends(cid: string): PendingSend[] {
  // One key, never the whole record — see the store's note on identity.
  return usePendingSendsStore((state) => state.byChannel[cid] ?? NO_PENDING);
}
